import styled from '@emotion/styled';

import { Argument, ArgumentBase } from '../flow-arguments/Argument';
import { FlowToken } from '../flow-arguments/FlowToken';

import { su } from '../../../theme/functions/su';
import { glyph } from '../../../theme/glyph';
import { motion } from '../../../theme/motion';
import { theme } from '../../../theme/theme';

import { PopularBadge } from '../../../components/badges/PopularBadge';
import { Glyph } from '../../../components/common/Glyph';
import { HomeyIcon } from '../../../components/common/HomeyIcon';
import { PremiumLocked } from '../../../components/premium/PremiumLocked';

import { createCard } from './createCard';
import { FlowCardBase } from './FlowCardBase';
import { FlowCardHint } from './FlowCardHint';
import { FlowCardTokensHint } from './FlowCardTokensHint';
import { useTitleChunks } from './useTitleChunks';

export const StaticFlowCard = createCard(StaticFlowCardInner);

export function StaticFlowCardInner(props) {
  const { titleChunks } = useTitleChunks({
    card: props.card,
    data: null,
  });

  function mapper(chunk, index) {
    if (chunk.type === 'text') {
      return <FlowCardBase.Text key={index}>{chunk.value}</FlowCardBase.Text>;
    }

    if (chunk.type === 'argument') {
      return (
        <ArgumentBase key={index} data-is-example={true}>
          {chunk.argumentTypeText}
        </ArgumentBase>
      );
    }

    if (chunk.type === 'droptoken') {
      if (props.droptoken != null) {
        return (
          <ArgumentBase key={index} data-is-droptoken={true}>
            <FlowToken token={props.droptoken} label={props.droptoken.title} />
          </ArgumentBase>
        );
      }

      return (
        <ArgumentBase key={index} data-is-example={true}>
          <Argument.IconWrapper>
            <Glyph source={glyph.core.tagFill} size={theme.icon.size_small} />
          </Argument.IconWrapper>

          {chunk.argumentTypeText}
        </ArgumentBase>
      );
    }

    return (
      <ArgumentBase key={index} data-is-example={true}>
        ...
      </ArgumentBase>
    );
  }

  return (
    <StaticFlowCard.Root
      style={{ '--card-width': props.cardWidth }}
      data-key={props.card.id}
      data-is-focused={props.isFocused}
      data-is-focus-visible={props.isFocusVisible}
    >
      <FlowCardBase.IconWrapper
        style={{
          '--flow-card-icon-wrapper-background': props.iconBackgroundColor,
        }}
      >
        {(() => {
          if (props.iconComponent != null) {
            return props.iconComponent;
          }

          if (props.iconUrl != null) {
            return (
              <Glyph
                source={props.iconUrl}
                color={theme.color.white}
                size={props.iconSize ?? theme.icon.size_medium}
              />
            );
          }

          return (
            <HomeyIcon
              iconObj={props.iconObj}
              color={theme.color.white}
              size={props.iconSize ?? theme.icon.size_default}
            />
          );
        })()}
      </FlowCardBase.IconWrapper>

      <FlowCardBase.Details>
        <FlowCardBase.Header>
          <FlowCardBase.Name>{props.name}</FlowCardBase.Name>
          {props.card.hint && <FlowCardHint hint={props.card.hint} />}
          {props.card.tokens != null && props.card.tokens?.length > 0 && (
            <FlowCardTokensHint tokens={props.card.tokens} />
          )}
        </FlowCardBase.Header>
        <FlowCardBase.Content>{titleChunks.map(mapper)}</FlowCardBase.Content>
      </FlowCardBase.Details>
      {props.card.highlight && <PopularBadge />}

      {props.isLocked && <PremiumLocked zIndex={2} />}
    </StaticFlowCard.Root>
  );
}

StaticFlowCard.Root = styled.div`
  --flow-token-max-width: 240px; // Used in src/containers/flows/flow-arguments/FlowToken.js
  position: relative;
  box-shadow: ${theme.boxShadow.default};
  background-color: ${theme.color.component};
  border-radius: ${theme.borderRadius.default};
  display: flex;
  flex: 0 0 auto;
  width: var(--card-width, 340px);
  padding: ${su(1)};
  cursor: pointer;
  outline: 0;
  background: ${theme.flowcard.background};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: -1;
    border-radius: ${theme.borderRadius.default};
    box-shadow: ${theme.boxShadow.default};
    opacity: 0;
    transition: ${motion.duration.fast} ${motion.curve.fast_in};
    transition-property: opacity;
    pointer-events: none;
  }

  &[data-is-focus-visible='true'] {
    background-color: ${theme.color.background_hover};
  }

  &[data-is-focused='true'],
  &:hover {
    &::before {
      opacity: 0.5;
    }
  }
`;
